import type { Dispatch, Middleware } from 'redux';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from './fire-error-analytics.tsx';

type FamaReduxMiddlewarePayload<A> = {
	attributes?: AnalyticsAttributes;
	errorActionCreator?: () => A;
	id: string;
	onError?: (error: Error) => void;
	packageName: string;
	sendToPrivacyUnsafeSplunk?: boolean;
	teamName?: string;
};

/**
 * Catches errors within redux reducers and makes them available to monitoring.
 *
 * As opposed to redux-middleware, this function uses fireErrorAnalytics to make data also available in SignalFx.
 *
 * Takes an optional error action creator as a parameter to be reduced when an error is encountered.
 * This could be used to show an error flag, for example.
 *
 * Should be passed as part of the middleware array when creating the store.
 *
 * e.g. [middlewareOne, errorHandling({ packageName: 'yourPackageName', id: 'yourErrorId' })]
 */

const errorHandling =
	<
		S,
		A extends {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			type: any;
		},
	>({
		attributes,
		errorActionCreator,
		id,
		onError,
		packageName,
		sendToPrivacyUnsafeSplunk,
		teamName,
	}: // @ts-expect-error - TS2315 - Type 'Middleware' is not generic.
	FamaReduxMiddlewarePayload<A>): Middleware<S, A> =>
	() =>
	(next: Dispatch<A>) =>
	(action: A) => {
		try {
			next(action);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			const defaultAttributes: AnalyticsAttributes = {
				message: `Unhandled error for Redux action: '${action.type}'`,
			};

			fireErrorAnalytics({
				error,
				meta: {
					id,
					packageName,
					teamName,
				},
				attributes: {
					...defaultAttributes,
					...attributes,
				},
				sendToPrivacyUnsafeSplunk,
			});

			errorActionCreator && next(errorActionCreator());
			onError && onError(error);
		}
	};
export default errorHandling;
